import Navbar from "components/Navbar";

import React, { useState } from "react";

import { ApiErrors } from "common/api-errors/errors";
import { CommonButton } from "common/components";
import { CommonErrorDialog } from "common/components/CommonErrorDialog";
import { ContentWrapper } from "components/ContentWrapper";
import { errorMessages } from "common/utils/validation";
import { Formik, Form } from "formik";
import { FormProgress } from "shared/form-progress";
import { Investment } from "common/store/investment/types";
import { NewInvestment } from "common/graphql/queryDescriptors";
import { Property } from "../../../common/store/property/types";
import { RegisterUserAccountsForm } from "pages/kyc/subforms";
import { SAVE_SHARES_INFO_ACTION } from "common/store/investment/actions";
import { SharesSubform, TermsSubform } from "./subforms/index";
import { StoreState } from "../../../common/store/types";
import { SummarySubform } from "./subforms/SummarySubform";
import { useAddToWhitelist } from "common/store/user/hooks/useAddToWhitelist";
import { useCustomQuery } from "common/store/hooks/useCustomQuery";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { User } from "common/store/user/types";
import { useTranslation } from "react-i18next";
import { Wallet } from "ethers";

import {
  Box,
  Grid,
  Typography,
  Card,
  CircularProgress,
} from "@material-ui/core";

import "./PurchaseSharesForm.scss";

enum PurchaseSharesStep {
  Shares = 0,
  Terms,
  Summary,
  UserPaymentAccounts,
  Count,
}

const PurchaseSharesSubform = ({
  type,
  values,
  errors,
  touched,
  setFieldValue,
  property,
  onAddPaymentMethods,
}: any): any => {
  switch (type) {
    case PurchaseSharesStep.Shares:
      if (values instanceof Investment) {
        return (
          <SharesSubform
            setFieldValue={setFieldValue}
            errors={errors}
            touched={touched}
            values={values}
            property={property}
          />
        );
      }
      break;
    case PurchaseSharesStep.Terms:
      if (values instanceof Investment) {
        return (
          <TermsSubform
            setFieldValue={setFieldValue}
            errors={errors}
            touched={touched}
            values={values}
          />
        );
      }
      break;
    case PurchaseSharesStep.Summary:
      if (values instanceof Investment) {
        return (
          <SummarySubform
            setFieldValue={setFieldValue}
            errors={errors}
            touched={touched}
            values={values}
            onAddPaymentMethods={onAddPaymentMethods}
          />
        );
      }
      break;
    case PurchaseSharesStep.UserPaymentAccounts:
      return <RegisterUserAccountsForm />;
    default:
      break;
  }

  return "";
};

export const PurchaseSharesForm = () => {
  //State
  const [error, setError] = useState("");
  const [step, setStep] = useState(PurchaseSharesStep.Shares);

  //Hooks
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const property = useSelector<StoreState, Property | undefined>((state) =>
    state.properties.find((p) => p.id === id)
  );
  const storageUserDetails = useSelector<StoreState, User>(
    (state) => state.currentUser
  );

  const { execute: executeInvestment, loading } = useCustomQuery(
    NewInvestment({
      onError: (error: ApiErrors) => {
        switch (error) {
          case ApiErrors.INVEST_TAC_NOT_ACCEPTED:
            setError(t("Terms not accepted"));
            setStep(PurchaseSharesStep.Terms);
            break;
          case ApiErrors.INVEST_AMOUNT_EXCEEDS_SHARES:
          case ApiErrors.INVEST_AMOUNT_EXCEEDS_MAXIMAL:
            setError(t("Maximum amount of shares exceeded"));
            setStep(PurchaseSharesStep.Shares);
            break;
          case ApiErrors.INVEST_AMOUNT_LOWER_MINIMAL:
            setError(t("Amount of shares lower than minimum"));
            setStep(PurchaseSharesStep.Shares);
            break;
          case ApiErrors.INVEST_NO_PAYMENT:
            setError(t("Payment unknown"));
            setStep(PurchaseSharesStep.Summary);
            break;
          default:
            break;
        }
      },
      onSuccess: async () => {
        const randomAddress = await Wallet.createRandom().getAddress();
        await executeAddToWhitelist({ variables: { address: randomAddress } });
      },
    })
  );

  const {
    executeAddToWhitelist,
    loading: whitelistLoading,
  } = useAddToWhitelist({
    onSuccess: () => {
      history.goBack();
    },
  });

  //Options
  const initialValues = new Investment(
    property
      ? {
          sale_id: property.id,
          amount: property.min,
          propertyPrice: property.price,
          minAmount: property.min,
          maxAmount: Math.min(
            property.max,
            property.cap - property.total_sold,
            Math.floor(1000 / property.price)
          ),
          payment: storageUserDetails.default_payment,
        }
      : null
  );

  //Helpers
  const nextStep = (): PurchaseSharesStep => {
    switch (step) {
      case PurchaseSharesStep.Shares:
        return PurchaseSharesStep.Terms;
      case PurchaseSharesStep.Terms:
        return PurchaseSharesStep.Summary;
      case PurchaseSharesStep.Summary:
        return step;
      case PurchaseSharesStep.UserPaymentAccounts:
        return PurchaseSharesStep.Summary;
      default:
        return step;
    }
  };

  const keys = (): Array<string> => {
    switch (step) {
      case PurchaseSharesStep.Shares:
        return ["amount"];
      case PurchaseSharesStep.Terms:
        return ["termsEffectaGmbh", "termsFinproAG"];
      case PurchaseSharesStep.Summary:
        return ["agreement1", "agreement2", "payment"];
      case PurchaseSharesStep.UserPaymentAccounts:
        return ["default_payment", "payment_accounts"];
      default:
        return [];
    }
  };

  const submitButtonText = (): string => {
    switch (step) {
      case PurchaseSharesStep.Shares:
      case PurchaseSharesStep.Terms:
        return t("Next");
      case PurchaseSharesStep.UserPaymentAccounts:
        return t("Back");
      case PurchaseSharesStep.Summary:
        return t("Complete purchase");
      default:
        return t("Next");
    }
  };

  const title = (propertyId: string): string => {
    switch (step) {
      case PurchaseSharesStep.Shares:
        return t(`Property #${propertyId}`);
      case PurchaseSharesStep.Terms:
        return t("Terms of Service");
      case PurchaseSharesStep.Summary:
        return t("Review Deposit");
      case PurchaseSharesStep.UserPaymentAccounts:
        return t("User accounts");
      default:
        return "";
    }
  };

  const subtitle = (): string => {
    switch (step) {
      case PurchaseSharesStep.Shares:
        return t("Location | Funding");
      case PurchaseSharesStep.Terms:
      case PurchaseSharesStep.UserPaymentAccounts:
        return "";
      case PurchaseSharesStep.Summary:
        return t("Location | Funding");
      default:
        return "";
    }
  };

  //Validation
  const onValidate = (values: Investment) => {
    return new Investment(values).validate(keys(), errorMessages);
  };

  const onSubmit = async (
    values: Investment,
    { setSubmitting, setTouched }: any
  ) => {
    setSubmitting(true);

    switch (step) {
      case PurchaseSharesStep.Shares:
      case PurchaseSharesStep.Terms:
        dispatch({
          type: SAVE_SHARES_INFO_ACTION,
          payload: { currentShare: new Investment(values) },
        });

        setStep(nextStep());
        setTouched({});
        break;
      case PurchaseSharesStep.UserPaymentAccounts:
        setStep(nextStep());
        setTouched({});
        break;
      case PurchaseSharesStep.Summary:
        dispatch({
          type: SAVE_SHARES_INFO_ACTION,
          payload: { currentShare: new Investment(values) },
        });

        const data = new Investment(values).addRequest();
        await executeInvestment({
          variables: data,
        });
        break;
      default:
        break;
    }

    setSubmitting(false);
  };

  const goToUserPaymentAccounts = (setTouched: any) => {
    setStep(PurchaseSharesStep.UserPaymentAccounts);
    setTouched({});
  };

  return (
    <Formik
      initialValues={initialValues}
      validate={onValidate}
      onSubmit={onSubmit}
    >
      {({
        touched,
        errors,
        submitForm,
        isSubmitting,
        setFieldValue,
        setTouched,
        values,
      }) => (
        <Box>
          <Navbar />

          <ContentWrapper>
            {property && (
              <Box className="purchase-shares">
                <Card className="card">
                  <Typography className="title--big" variant="subtitle1">
                    {title(property.id)}
                  </Typography>

                  <Typography className="subtitle--big" variant="subtitle2">
                    {subtitle()}
                  </Typography>

                  <Form>
                    <Grid justify="center" container spacing={5}>
                      <Grid item xs={12}>
                        <PurchaseSharesSubform
                          type={step}
                          values={values}
                          errors={errors}
                          touched={touched}
                          setFieldValue={setFieldValue}
                          property={property}
                          onAddPaymentMethods={() =>
                            goToUserPaymentAccounts(setTouched)
                          }
                        />
                      </Grid>

                      <Grid container item direction="row">
                        {loading || whitelistLoading ? (
                          <CircularProgress />
                        ) : (
                          <CommonButton
                            onClick={submitForm}
                            isSubmitting={isSubmitting}
                            values={values}
                            errors={errors}
                            touched={touched}
                            text={submitButtonText()}
                          />
                        )}
                      </Grid>
                    </Grid>
                  </Form>
                  <CommonErrorDialog error={error} setError={setError} />
                </Card>
                <FormProgress
                  maxStep={PurchaseSharesStep.Count - 1}
                  step={
                    step === PurchaseSharesStep.UserPaymentAccounts
                      ? PurchaseSharesStep.Summary
                      : step
                  }
                  onClick={(goToStep) => {
                    setTouched({});
                    setStep(goToStep);
                  }}
                />
              </Box>
            )}
          </ContentWrapper>
        </Box>
      )}
    </Formik>
  );
};
